<template>
  <router-link
    v-if="!isExternal && !isDisabled"
    class="bottom-nav-link flex flex-col items-center justify-center rounded-full text-center"
    :class="isActive ? [textColour, 'bottom-nav-link--active'] : 'text-grey-40'"
    :to="to"
    :data-event-tracking="dataEventTracking"
    :data-test-id="dataTestId"
    @click="clickEvent()"
  >
    <span
      v-if="typeof icon === 'string'"
      class="nav-icon-frame nav-icon-mask mb-1"
      :style="{ '--nav-icon': `url(${icon})`, '--nav-icon-size': iconSize }"
      role="img"
      :aria-label="label"
    ></span>
    <span v-else class="nav-icon-frame mb-1">
      <component :is="icon" class="nav-icon-component" :is-active="isActive" />
    </span>
    <span
      class="block text-center font-aktiv-grotesk-trial text-[10px] leading-[12px] md:text-[12px]"
    >
      {{ label }}
    </span>
  </router-link>
  <button
    v-else-if="isDisabled"
    type="button"
    class="bottom-nav-link flex flex-col items-center justify-center rounded-full text-center text-grey-40"
    :data-event-tracking="dataEventTracking"
    :data-test-id="dataTestId"
    @click="clickEvent()"
  >
    <span
      v-if="typeof icon === 'string'"
      class="nav-icon-frame nav-icon-mask mb-1"
      :style="{ '--nav-icon': `url(${icon})`, '--nav-icon-size': iconSize }"
      role="img"
      :aria-label="label"
    ></span>
    <span v-else class="nav-icon-frame mb-1">
      <component :is="icon" class="nav-icon-component" color="#99A4B2" />
    </span>
    <span
      class="block text-center font-aktiv-grotesk-trial text-[10px] leading-[12px] md:text-[12px]"
    >
      {{ label }}
    </span>
  </button>
  <a
    v-else
    class="bottom-nav-link flex flex-col items-center justify-center rounded-full text-center text-grey-40"
    :href="to"
    :data-event-tracking="dataEventTracking"
    :data-test-id="dataTestId"
    target="_blank"
  >
    <span
      v-if="typeof icon === 'string'"
      class="nav-icon-frame nav-icon-mask mb-1"
      :style="{ '--nav-icon': `url(${icon})`, '--nav-icon-size': iconSize }"
      role="img"
      :aria-label="label"
    ></span>
    <span v-else class="nav-icon-frame mb-1">
      <component :is="icon" class="nav-icon-component" color="#99A4B2" />
    </span>
    <span
      class="block text-center font-aktiv-grotesk-trial text-[10px] leading-[12px] md:text-[12px]"
    >
      {{ label }}
    </span>
  </a>
</template>

<script>
import { FirebaseAnalytics } from '@/utils/firebase-analytics-wrapper';
import mixpanel from 'mixpanel-browser';
import { isGICPage } from '@/utils/conference-type';

export default {
  props: {
    isExternal: {
      type: Boolean,
      default: false,
    },
    to: {
      type: String,
      default: null,
      required: true,
    },
    label: {
      type: String,
      default: null,
      required: true,
    },
    icon: {
      type: [Object, String],
      default: null,
      required: true,
    },
    dataTestId: {
      type: String,
      default: null,
    },
    dataEventTracking: {
      type: String,
      default: null,
    },
    iconSize: {
      type: String,
      default: null,
    },
    activePath: {
      type: String,
      default: null,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    isActive() {
      const activePath = this.activePath || this.to;

      // if resource route, need to check for resource
      if (this.$route.path.includes('resource') && activePath.includes('resource')) {
        return true;
      }

      const parentActive = this.$route.path.includes(activePath) && activePath !== '/';
      return this.$route.path === activePath || parentActive;
    },
  },
  methods: {
    clickEvent() {
      console.log('clickevent here...');
      this.$emit('clicked-nav-button');
      FirebaseAnalytics.logEvent({
        name: 'click_button',
        params: {
          button_name: this.dataEventTracking,
          page: 'bottomNav',
        },
      });
      mixpanel.track('Bottom Nav Button Clicked', {
        button_name: this.dataEventTracking,
        page: 'bottomNav',
      });
    },
  },
  data() {
    return {
      textColour: isGICPage() ? 'text-orange' : 'text-[#00A9E0]',
    };
  },
};
</script>

<style scoped>
.bottom-nav-link {
  appearance: none;
  background: transparent;
  border: 0;
  height: 58px;
  min-width: 0;
  padding: 8px 8px 7px;
  position: relative;
  transition: color 160ms ease;
  width: 100%;
}

.bottom-nav-link > * {
  position: relative;
  z-index: 1;
}

.bottom-nav-link::before {
  background: transparent;
  border-radius: 999px;
  content: '';
  inset: 1px -6px;
  opacity: 0;
  position: absolute;
  transition:
    background-color 160ms ease,
    box-shadow 160ms ease,
    opacity 160ms ease;
}

.bottom-nav-link--active::before {
  background: rgba(118, 118, 128, 0.12);
  opacity: 1;
}

.nav-icon-frame {
  align-items: center;
  display: flex;
  flex: 0 0 24px;
  height: 24px;
  justify-content: center;
  width: 24px;
}

.nav-icon-mask {
  background-color: currentColor;
  height: var(--nav-icon-size, 24px);
  -webkit-mask-image: var(--nav-icon);
  mask-image: var(--nav-icon);
  -webkit-mask-position: center;
  mask-position: center;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-size: var(--nav-icon-size, 24px) var(--nav-icon-size, 24px);
  mask-size: var(--nav-icon-size, 24px) var(--nav-icon-size, 24px);
  width: var(--nav-icon-size, 24px);
}

.nav-icon-frame.nav-icon-mask {
  display: block;
  flex: 0 0 var(--nav-icon-size, 24px);
}

.nav-icon-component {
  display: block;
  height: 24px;
  width: 24px;
}
</style>
